import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  Autocomplete,
  FormHelperText,
  Avatar,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { Business as BusinessIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import SelectLocation from "src/@core/components/SelectLocation";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AddressAutocompleteField from "src/@core/components/AddressAutocompleteField";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import BusinessUploads from "src/@core/components/OutlinedUploadBox";
import { SelectUser } from "./SelectUser";
import useSubscriptionCheck from "src/hooks/useSubscriptionCheck";
import files from "src/utils/constants";
import { useTheme } from "@emotion/react";
import ChangeEmailDialog from "src/@core/components/ChangeEmailDialog";
import OtpVerificationDialog from "src/@core/components/OtpVerificationDialog";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  MAX_BUSINESS_MEDIA_ITEMS,
  MAX_BUSINESS_VIDEO_DURATION,
  getBusinessMediaType,
  getVideoDuration,
  normalizeBusinessMedia,
  toBusinessMediaPayload,
} from "src/utils/businessMedia";
import { BUSINESS_MEDIA } from "src/utils/uploadCategories";

// Helper function to generate UUID (with fallback for older browsers)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Helper function to generate unique filename with UUID
const generateUniqueFileName = (originalFileName) => {
  const lastDotIndex = originalFileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < originalFileName.length - 1;
  
  let fileExtension = '';
  if (hasExtension) {
    fileExtension = originalFileName.substring(lastDotIndex + 1).toLowerCase();
  }
  
  const uuid = generateUUID();
  return hasExtension ? `${uuid}.${fileExtension}` : uuid;
};

// Hook to get presigned URL
const usePresignedUrl = () => {
  return useMutation({
    mutationFn: async ({ category, fileName, contentType }) => {
      const res = await axiosInstance.post(
        ApiEndPoints.UPLOAD.presignedUrl,
        {
          category,
          fileName,
          contentType,
        }
      );
      return res.data.data;
    },
  });
};

// Hook to confirm upload
const useConfirmUpload = () => {
  return useMutation({
    mutationFn: async ({ fileKey, category }) => {
      const res = await axiosInstance.post(
        ApiEndPoints.UPLOAD.confirm,
        {
          fileKey,
          category,
        }
      );
      return res.data.data;
    },
  });
};


// Hook to upload file using presigned URL
const useUploadFileWithPresignedUrl = () => {
  const { mutate: getPresignedUrl } = usePresignedUrl();
  const { mutate: confirmUpload } = useConfirmUpload();

  return useMutation({
    mutationFn: async ({ file, category }) => {
      return new Promise((resolve, reject) => {
        // Generate unique filename with UUID to avoid conflicts
        const fileName = generateUniqueFileName(file.name);
        const contentType = file.type || 'image/png';

        // Step 1: Get presigned URL with UUID filename
        getPresignedUrl(
          { category, fileName, contentType },
          {
            onSuccess: async (presignedData) => {
              try {
                // Step 2: Upload file to S3 using presigned URL
                const url = new URL(presignedData.uploadUrl);
                const urlContentType = url.searchParams.get('Content-Type');
                const uploadContentType = urlContentType ? decodeURIComponent(urlContentType) : contentType;
                
                // Use XMLHttpRequest for S3 upload
                // Step 2: Upload file to S3 - MUST complete successfully before confirming
                await new Promise((resolve, reject) => {
                  const xhr = new XMLHttpRequest();
                  
                  xhr.open('PUT', presignedData.uploadUrl, true);
                  xhr.setRequestHeader('Content-Type', uploadContentType);
                  
                  xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                      // S3 upload successful - resolve to continue to confirm step
                      resolve();
                    } else {
                      const errorMessage = xhr.responseText || xhr.statusText;
                      console.error('S3 Upload Error:', {
                        status: xhr.status,
                        statusText: xhr.statusText,
                        responseText: xhr.responseText,
                        uploadUrl: presignedData.uploadUrl,
                        contentType: uploadContentType,
                      });
                      reject(
                        new Error(
                          `Failed to upload file to S3: ${xhr.status} ${xhr.statusText}. ${errorMessage}`
                        )
                      );
                    }
                  };
                  
                  xhr.onerror = (event) => {
                    const isCorsError = xhr.status === 0 && !xhr.responseText;
                    console.error('S3 Upload CORS Error:', {
                      isCorsError,
                      status: xhr.status,
                      uploadUrl: presignedData.uploadUrl,
                      contentType: uploadContentType,
                      origin: window.location.origin,
                    });
                    
                    if (isCorsError) {
                      const corsConfig = {
                        AllowedHeaders: ['Content-Type'],
                        AllowedMethods: ['PUT', 'OPTIONS'],
                        AllowedOrigins: [window.location.origin, 'http://localhost:3000'],
                        ExposeHeaders: ['ETag'],
                        MaxAgeSeconds: 3000,
                      };
                      
                      reject(
                        new Error(
                          `CORS Error: S3 bucket 'beemzbucket' must be configured to allow PUT requests from origin '${window.location.origin}'. ` +
                          `Please configure CORS on the S3 bucket with: ${JSON.stringify(corsConfig, null, 2)}`
                        )
                      );
                    } else {
                      reject(new Error(`Network error while uploading file to S3: ${xhr.status} ${xhr.statusText}`));
                    }
                  };
                  
                  xhr.ontimeout = () => {
                    reject(new Error('Upload to S3 timed out after 30 seconds'));
                  };
                  
                  xhr.onabort = () => {
                    reject(new Error('Upload to S3 was aborted'));
                  };
                  
                  xhr.timeout = 30000;
                  xhr.send(file);
                });

                // Step 3: ONLY confirm upload if S3 upload succeeded
                // This will only execute if the await above resolves (not rejects)
                confirmUpload(
                  {
                    fileKey: presignedData.fileKey,
                    category,
                  },
                  {
                    onSuccess: (confirmData) => {
                      // Ensure fileKey starts with "/" for proper storage format
                      const fileKey = confirmData.fileKey.startsWith('/') 
                        ? confirmData.fileKey 
                        : `/${confirmData.fileKey}`;
                      
                      resolve({
                        fileUrl: confirmData.fileUrl,
                        fileKey: fileKey,
                      });
                    },
                    onError: (error) => {
                      reject(error);
                    },
                  }
                );
              } catch (error) {
                reject(error);
              }
            },
            onError: (error) => {
              reject(error);
            },
          }
        );
      });
    },
  });
};

const useAddLocation = () => {
  return useMutation({
    mutationFn: async ({
      businessId,
      data,
      isEdit = false,
      locationId = null,
    }) => {
      const url = isEdit
        ? `${ApiEndPoints.BUSINESS_LOCATION.add}/${locationId}`
        : `${ApiEndPoints.BUSINESS_LOCATION.add}/${businessId}`;
      const method = isEdit ? "patch" : "post";
      const res = await axiosInstance[method](url, data);
      return res.data;
    },
  });
};

const useUsernameSuggestions = () => {
  return useMutation({
    mutationFn: async (input) => {
      const res = await axiosInstance.post(
        `${ApiEndPoints.BUSINESS.userName}`,
        { input }
      );
      return res.data;
    },
  });
};

const inputSx = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    background: "#F4F5FA",
    fontSize: "14px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ECECEC",
  },
  "& .MuiInputBase-input": {
    padding: "18.5px 14px",
  },
  "& .MuiFormLabel-root": {
    color: "#888",
  },
};

const daysOfWeek = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const AddLocation = ({ businessLocationData, locationToEdit, onBack }) => {
  const { t } = useTranslation("dashboard");
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [options, setOptions] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState(null);
  const [usernameState, setUsernameState] = useState({
    isValid: true,
    error: "",
  });
  const [changeEmailDialogOpen, setChangeEmailDialogOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeError, setEmailChangeError] = useState("");

  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  
  const businessSubscriptions = useSelector((state) => state.auth.businessSubscriptions || {});
  const { checkLimit } = useSubscriptionCheck();

  const user = useSelector((state) => state.auth.user);

  const queryClient = useQueryClient();
  const { mutate: getUsernameSuggestions } = useUsernameSuggestions();
  const { mutate } = useAddLocation();
  const uploadFileMutation = useUploadFileWithPresignedUrl();
  const { mutate: uploadFileWithPresignedUrl, mutateAsync: uploadFileWithPresignedUrlAsync } = uploadFileMutation;
  
  const [bannerFileKey, setBannerFileKey] = useState(null);
  const [profileFileKey, setProfileFileKey] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [media, setMedia] = useState([]);
  
  // Email change mutations
  const sendOtpMutation = useMutation({
    mutationFn: async (email) => {
      const response = await axiosInstance.post(ApiEndPoints.BUSINESS.change, {
        userName: {
          2: email, // UserData.EMAIL = 2
        },
        device: 1,
        businessId: selectedBusinessId,
      });
      return response.data;
    },
    onSuccess: () => {
      setOtpDialogOpen(true);
      setChangeEmailDialogOpen(false);
      setEmailChangeError("");
      toast.success(t("otpSentToEmail"));
    },
    onError: (error) => {
      setEmailChangeError(
        error?.response?.data?.message || t("failedToSendOtp")
      );
      toast.error(error?.response?.data?.message || t("failedToSendOtp"));
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (otp) => {
      const response = await axiosInstance.patch(
        ApiEndPoints.BUSINESS.changeVerify(selectedBusinessId),
        {
          otp: otp,
          userName: {
            2: newEmail, // UserData.EMAIL = 2
          },
          device: 1,
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(t("emailHasBeenChanged"));
      setValue("emailAddress", newEmail);
      setOtpDialogOpen(false);
      setNewEmail("");
      setEmailChangeError("");
      // Invalidate queries to refresh data
      queryClient.invalidateQueries([
        "businessLocationData",
        selectedBusinessId,
      ]);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || t("failedToVerifyOtp")
      );
    },
  });
  
  // console.log('locationToEdit', locationToEdit);

  // Normalize businessId to string (API expects 24-char hex; locationToEdit.businessId may be populated object)
  const businessIdForApi =
    typeof locationToEdit?.businessId === "string"
      ? locationToEdit.businessId
      : locationToEdit?.businessId?._id ?? null;

  const { data: linkedUserList = [] } = useQuery(
    ["linkedUserList", businessIdForApi],
    async () => {
      const url = ApiEndPoints.TEAM_LINKING.get_team_member(businessIdForApi);
      const body = {
        locationIds: [locationToEdit?._id],
      };
      const res = await axiosInstance.post(url, body);
      return res.data.data.employees;
    },
    {
      enabled: locationToEdit !== null && !!businessIdForApi,
    }
  );
  // console.log('linkedUserList', linkedUserList);

  const getDefaultValues = (editData = null) => {
    if (!editData) {
      return {
        businessLocationName: "",
        businessLocationUsername: "",
        physicalAddress: "",
        description: "",
        emailAddress: "",
        phoneNumber: "+91",
        status: "open",
        customStatus: "",
        businessVatNumber: "",
        businessTaxReferenceNumber: "",
        businessRegistrationNumber: "",
        scheduleType: "always_open",
        locationBannerPhoto: "",
        locationProfilePhoto: "",
        locationBannerPhotoKey: "",
        locationProfilePhotoKey: "",
        isTemporaryClosed: false,
        isMobileAddress: true,
        selectedUser: null,
        locationData: {
          longitude: null,
          latitude: null,
          physicalAddress: "",
        },
        scheduleDays: daysOfWeek.map((day) => ({
          day,
          isOpen: true,
          is24Hours: true,
          openTime: "",
          closeTime: "",
        })),
      };
    }

    const isAlwaysOpen = editData.schedule?.type === "24_hours_all_days";

    // const matchedUsers = data.filter(item =>
    //   linkedUserList.includes(item.userId._id)
    // );

    //
    let selectedUser = null;

    if (editData.isMobileAddress && editData.userIdForLocation) {
      console.log("matchedUser--", editData.userIdForLocation);

      const matchedUser = linkedUserList.find(
        (item) => item.userId._id === editData.userIdForLocation
      );
      console.log("matchedUser---", linkedUserList);
      console.log("matchedUser", matchedUser);
      if (matchedUser) {
        selectedUser = {
          _id: matchedUser.userId._id,
          profilePic: matchedUser.userId.profilePic,
          fullName: matchedUser.userId.fullName,
          userName: matchedUser.userId.userName,
        };
      } else {
        if (editData.userIdForLocation === user.userId) {
          selectedUser = {
            _id: user.userId,
            profilePic: user.profilePic,
            fullName: user.fullName,
            userName: user.userName,
          };
        }
      }
    }

    // console.log('selectedUser--', editData.userIdForLocation);
    // console.log('selectedUser--', user.userId);

    return {
      businessLocationName: editData.businessLocationName || "",
      businessLocationUsername: editData.businessLocationUsername || "",
      physicalAddress: editData.physicalAddress || "",
      description: editData.description || "",
      emailAddress: editData.emailAddress || "",
      phoneNumber: editData.phoneNumber
        ? `${editData.phoneNumber.countryCode}${editData.phoneNumber.number}`
        : "+91",
      status: ["open", "close"].includes(editData.status) ? editData.status : "custom",
      customStatus: ["open", "close"].includes(editData.status)
        ? ""
        : (editData.status || ""),
      businessVatNumber: editData.businessVatNumber || "",
      businessTaxReferenceNumber: editData.businessTaxReferenceNumber || "",
      businessRegistrationNumber: editData.businessRegistrationNumber || "",
      scheduleType: isAlwaysOpen ? "always_open" : "custom",
      locationBannerPhoto: editData.locationBannerPhoto || "",
      locationProfilePhoto: editData.locationProfilePhoto || "",
      locationBannerPhotoKey: editData.locationBannerPhotoKey || "",
      locationProfilePhotoKey: editData.locationProfilePhotoKey || "",
      isTemporaryClosed: editData.isTemporaryClose || false,
      isMobileAddress: editData.isMobileAddress,
      selectedUser: selectedUser, // Initialize as null, will be set if needed
      locationData: {
        longitude: editData.location?.coordinates?.[0] || null,
        latitude: editData.location?.coordinates?.[1] || null,
        physicalAddress: editData.physicalAddress || "",
      },
      scheduleDays: daysOfWeek.map((day) => {
        const foundDay =
          editData.schedule?.days.find((d) => d.day === day) || {};
        return {
          day,
          isOpen: foundDay.isOpen ?? true,
          is24Hours: foundDay.is24Hours ?? true,
          openTime: foundDay.openTime || "",
          closeTime: foundDay.closeTime || "",
        };
      }),
    };
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: getDefaultValues(locationToEdit),
  });

  // Initialize currency and file keys from locationToEdit when editing
  useEffect(() => {
    if (locationToEdit) {
      if (locationToEdit.currency) {
        setCurrency(locationToEdit.currency);
      }
      if (locationToEdit.currencySymbol) {
        setCurrencySymbol(locationToEdit.currencySymbol);
      }
      // Initialize file keys and previews if they exist
      if (locationToEdit.locationBannerPhoto) {
        setBannerFileKey(locationToEdit.locationBannerPhoto);
        setBannerPreview(locationToEdit.locationBannerPhoto);
      }
      if (locationToEdit.locationProfilePhoto) {
        setProfileFileKey(locationToEdit.locationProfilePhoto);
        setProfilePreview(locationToEdit.locationProfilePhoto);
      }
      setMedia(
        normalizeBusinessMedia(locationToEdit.media, locationToEdit.photos)
      );
    }
  }, [locationToEdit]);

  // Watch form values
  const watchedValues = watch();
  console.log('watchedValues', watchedValues);
  const {
    scheduleType,
    scheduleDays = [],
    isTemporaryClosed,
    isMobileAddress,
    status,
    selectedUser,
    locationBannerPhoto,
    locationProfilePhoto,
    locationData,
  } = watchedValues;

  // Memoized debounced search function
  const handleSearch = useMemo(
    () =>
      debounce((value) => {
        if (value?.trim()) {
          getUsernameSuggestions(value, {
            onSuccess: (data) => {
              setOptions(data?.data?.data);
              setUsernameState({
                isValid: data.message !== "user name already taken",
                error:
                  data.message === "user name already taken"
                    ? data.message
                    : "",
              });
            },
          });
        }
      }, 500),
    [getUsernameSuggestions]
  );

  const handleFileUpload = async (event, fieldName) => {
    const file = event.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      if (fieldName === "locationBannerPhoto") {
        setBannerPreview(e.target.result);
      } else if (fieldName === "locationProfilePhoto") {
        setProfilePreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);

    // Upload new file
    const category = fieldName === "locationBannerPhoto" ? "banner" : "business-profile";
    
    try {
      toast.loading(fieldName === "locationBannerPhoto" ? t("uploadingBannerImage") : t("uploadingProfileImage"), {
        id: `upload-${fieldName}`,
      });

      uploadFileWithPresignedUrl(
        { file, category },
        {
          onSuccess: ({ fileUrl, fileKey }) => {
            toast.success(t("imageUploadedSuccessfully"), {
              id: `upload-${fieldName}`,
            });

            // Use fileUrl from API (full S3 URL) for preview – no IMAGE_URL
            if (fieldName === "locationBannerPhoto") {
              setBannerPreview(fileUrl || null);
              const formattedFileKey = fileKey.startsWith('/') ? fileKey : `/${fileKey}`;
              setBannerFileKey(formattedFileKey);
              setValue("locationBannerPhotoKey", formattedFileKey);
            } else if (fieldName === "locationProfilePhoto") {
              setProfilePreview(fileUrl || null);
              const formattedFileKey = fileKey.startsWith('/') ? fileKey : `/${fileKey}`;
              setProfileFileKey(formattedFileKey);
              setValue("locationProfilePhotoKey", formattedFileKey);
            }
          },
          onError: (error) => {
            toast.error(
              error?.response?.data?.message || t("failedToUploadImage"),
              { id: `upload-${fieldName}` }
            );
            // Reset preview on error
            if (fieldName === "locationBannerPhoto") {
              setBannerPreview(null);
            } else if (fieldName === "locationProfilePhoto") {
              setProfilePreview(null);
            }
          },
        }
      );
    } catch (error) {
      toast.error(t("failedToUploadImage"), {
        id: `upload-${fieldName}`,
      });
    }
  };

  const handleFileDelete = (fieldName) => {
    if (fieldName === "locationBannerPhoto") {
      setBannerPreview(null);
      setBannerFileKey(null);
      setValue("locationBannerPhoto", "");
      setValue("locationBannerPhotoKey", "");
    } else if (fieldName === "locationProfilePhoto") {
      setProfilePreview(null);
      setProfileFileKey(null);
      setValue("locationProfilePhoto", "");
      setValue("locationProfilePhotoKey", "");
    }
  };

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const currentTotal = media.length;

    const isValid = await checkLimit({
      accountType: `business_${selectedBusinessId}`,
      featureKey: "business_photos_videos_per_location",
      customCurrentUsage: currentTotal,
      limitDetails: {
        dialogMessage: (limit) => `You have reached your location media limit of ${limit}. Upgrade your plan to add more photos and videos.`,
        toastMessage: (limit) => `Media limit reached! You can only have ${limit} media items per location.`,
        successMessage: null
      }
    });

    if (!isValid) {
      event.target.value = "";
      return;
    }

    // Determine the actual limit for slicing
    // fallback to MAX_BUSINESS_MEDIA_ITEMS if feature is not found or isUnlimited
    const subscriptionInfo = businessSubscriptions[selectedBusinessId];
    const feature = subscriptionInfo?.subscriptionPlan?.features?.find(
      (f) => f.featureKey === "business_photos_videos_per_location"
    );
    
    let dynamicLimit = MAX_BUSINESS_MEDIA_ITEMS;
    if (feature && feature.enabled && !feature.isUnlimited) {
      dynamicLimit = feature.quantity;
    }

    const remainingSlots = dynamicLimit - currentTotal;
    const filesToUpload = files.slice(0, remainingSlots);

    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more media file(s) can be uploaded.`);
    }

    toast.loading("Uploading location media...", { id: "location-media-upload" });

    try {
      const uploadedMedia = [];
      for (const file of filesToUpload) {
        const mediaType = getBusinessMediaType(file);

        if (!mediaType) {
          toast.error(`${file.name} is not a supported image or video file.`);
          continue;
        }

        let duration = 0;
        if (mediaType === "video") {
          duration = await getVideoDuration(file);
          if (duration > MAX_BUSINESS_VIDEO_DURATION) {
            toast.error(
              `${file.name} exceeds the ${MAX_BUSINESS_VIDEO_DURATION}-second video limit.`
            );
            continue;
          }
        }

        const result = await uploadFileWithPresignedUrlAsync({
          file,
          category: BUSINESS_MEDIA,
        });
        if (result?.fileUrl) {
          uploadedMedia.push({
            type: mediaType,
            url: result.fileUrl,
            preview: result.fileUrl,
            size: file.size || 0,
            duration: mediaType === "video" ? duration : undefined,
          });
        }
      }

      if (uploadedMedia.length > 0) {
        setMedia((prev) =>
          [...prev, ...uploadedMedia].slice(0, MAX_BUSINESS_MEDIA_ITEMS)
        );
      }
      toast.success("Location media uploaded.", { id: "location-media-upload" });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to upload some media.",
        { id: "location-media-upload" }
      );
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveMedia = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
    toast.success("Media removed.");
  };

  // Function to fetch currency by country name
  const fetchCurrencyByCountryName = async (countryName) => {
    if (!countryName) {
      console.log("No country name provided, skipping currency fetch");
      setCurrency(null);
      setCurrencySymbol(null);
      return;
    }

    try {
      console.log("=== FETCHING CURRENCY (ADD LOCATION) ===");
      console.log("Country name:", countryName);
      // Encode country name for URL
      const encodedCountryName = encodeURIComponent(countryName);
      const url = `${ApiEndPoints.COMMON.currency}/country/${encodedCountryName}`;
      console.log("API URL:", url);
      
      const response = await axiosInstance.get(url);
      
      console.log("=== CURRENCY API RESPONSE (ADD LOCATION) ===");
      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Response status:", response.data?.status);
      console.log("Currency object:", response.data?.data?.currency);

      if (response.data?.status === "success" && response.data?.data?.currency) {
        const currencyData = response.data.data.currency;
        console.log("=== CURRENCY DATA EXTRACTED (ADD LOCATION) ===");
        console.log("Full currency data:", currencyData);
        console.log("Currency code:", currencyData.currency);
        console.log("Currency symbol:", currencyData.symbol);
        console.log("ISO2:", currencyData.iso2);
        console.log("ISO3:", currencyData.iso3);
        console.log("Country name:", currencyData.name);
        
        setCurrency(currencyData.currency);
        setCurrencySymbol(currencyData.symbol);
        
        console.log("=== CURRENCY STATE UPDATED (ADD LOCATION) ===");
        console.log("Currency state set to:", currencyData.currency);
        console.log("CurrencySymbol state set to:", currencyData.symbol);
      } else {
        console.log("=== NO CURRENCY FOUND (ADD LOCATION) ===");
        console.log("Response status:", response.data?.status);
        console.log("Currency object:", response.data?.data?.currency);
        console.log("No currency found for country:", countryName);
        setCurrency(null);
        setCurrencySymbol(null);
      }
    } catch (error) {
      console.error("=== CURRENCY FETCH ERROR (ADD LOCATION) ===");
      console.error("Error:", error);
      console.error("Error message:", error?.message);
      console.error("Error response:", error?.response);
      console.error("Error response data:", error?.response?.data);
      console.error("Error response status:", error?.response?.status);
      setCurrency(null);
      setCurrencySymbol(null);
    }
  };

  const handleLocationSelect = async (locationObject) => {
    console.log("=== LOCATION SELECTED (ADD LOCATION) ===");
    console.log("Location object:", locationObject);
    console.log("Country name:", locationObject.countryName);
    console.log("ISO2:", locationObject.iso2);
    console.log("ISO3:", locationObject.iso3);
    
    setValue("locationData", {
      longitude: locationObject.longitude || null,
      latitude: locationObject.latitude || null,
      physicalAddress: locationObject.physicalAddress || "",
    });
    setValue("physicalAddress", locationObject.physicalAddress || "");

    // Fetch currency using country name only if country name is available
    // Don't clear currency if country name is missing (preserve existing currency)
    if (locationObject.countryName) {
      await fetchCurrencyByCountryName(locationObject.countryName);
    } else {
      console.log("No country name available in location object, preserving existing currency");
      // Don't clear currency - keep existing values
    }
  };

  const parsePhoneNumber = (phoneString) => {
    if (!phoneString) return null;

    // Remove spaces and non-digit characters
    const cleaned = phoneString.replace(/\D/g, "");

    // Must be at least country code + number
    if (cleaned.length <= 3) return null;

    const countryCode = cleaned.slice(0, 2); // assuming 2-digit country code like "91"
    const number = cleaned.slice(2);

    return {
      countryCode: `+${countryCode}`,
      number,
    };
  };

  // console.log('user---->', user);

  // FINAL PAYLOAD BUILDER (backend validation compliant)
  const createPayload = async (data) => {
    const phoneNumber = parsePhoneNumber(data.phoneNumber);

    const username =
      data.businessLocationUsername ||
      data.businessLocationName
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    // Use the uploaded fileKeys (paths) - ensure they start with "/"
    const bannerImageKey = bannerFileKey || data.locationBannerPhotoKey || data.locationBannerPhoto || "";
    const profileImageKey = profileFileKey || data.locationProfilePhotoKey || data.locationProfilePhoto || "";

    // physicalAddress, longitude, latitude always required by Joi, but can be ""/null for mobile address
    // For mobile address (isMobileAddress true): userIdForLocation required, location fields ""/null
    // For physical address (isMobileAddress false): userIdForLocation "" and location fields required

    // Always set locationData from form for physical address (no auto-detect)
    // Preserve location data from locationToEdit if form data is missing
    const finalLocationData = {
      physicalAddress:
        data.locationData?.physicalAddress || 
        data.physicalAddress || 
        (locationToEdit?.physicalAddress || ""),
      longitude: data.locationData?.longitude ?? 
        (locationToEdit?.location?.coordinates?.[0] ?? null),
      latitude: data.locationData?.latitude ?? 
        (locationToEdit?.location?.coordinates?.[1] ?? null),
    };

    // Build payload
    const payload = {
      businessLocationName: data.businessLocationName,
      businessLocationUsername: username,
      description: data.description || "",
      isMobileAddress: !!isMobileAddress,
      emailAddress: data.emailAddress || "",
      phoneNumber: phoneNumber,
      status:
        data.status === "custom"
          ? (data.customStatus || "").trim()
          : (data.status || "open"),
      businessVatNumber: data.businessVatNumber || "",
      businessTaxReferenceNumber: data.businessTaxReferenceNumber || "",
      businessRegistrationNumber: data.businessRegistrationNumber || "",
      locationBannerPhoto: bannerImageKey ? getPreviewUrl(bannerImageKey) : "",
      locationProfilePhoto: profileImageKey ? getPreviewUrl(profileImageKey) : "",
      isPrimaryAddress: false,
      isTemporaryClosed: !!data.isTemporaryClosed,
      userIdForLocation: isMobileAddress ? selectedUser?._id || "" : "",
      // Preserve currency from locationToEdit if state is empty (for edit mode)
      currency: currency || locationToEdit?.currency || "",
      currencySymbol: currencySymbol || locationToEdit?.currencySymbol || "",
      media: toBusinessMediaPayload(media),
    };

    // Only add location fields when it's NOT a mobile address
    if (!isMobileAddress) {
      payload.physicalAddress = finalLocationData.physicalAddress;
      payload.longitude = finalLocationData.longitude;
      payload.latitude = finalLocationData.latitude;
    }

    console.log("=== ADD LOCATION PAYLOAD ===");
    console.log("Currency:", currency);
    console.log("Currency Symbol:", currencySymbol);
    console.log("Payload currency:", payload.currency);
    console.log("Payload currencySymbol:", payload.currencySymbol);

    // Conditionally add schedule as per Joi
    if (!payload.isTemporaryClosed) {
      payload.schedule = {
        type: scheduleType === "always_open" ? "24_hours_all_days" : "custom",
        days: (scheduleDays || []).map((dayData) => ({
          day: dayData.day,
          isOpen: dayData.isOpen,
          is24Hours: dayData.is24Hours,
          openTime: dayData.openTime || "",
          closeTime: dayData.closeTime || "",
        })),
        hasAdvanceSettings: false,
        specialHours: [],
        timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Calcutta",
      };
    }

    return payload;
  };

  // Validate as per backend
  const validateRequiredFields = () => {
    if (isMobileAddress) {
      if (!selectedUser || !selectedUser._id) {
        toast.error(t("pleaseSelectUserForMobileAddress"));
        return false;
      }
    } else {
      if (
        !locationData ||
        !locationData.latitude ||
        !locationData.longitude ||
        !locationData.physicalAddress
      ) {
        toast.error(t("pleaseSelectValidPhysicalAddress"));
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (data) => {
    // Custom validation
    if (!validateRequiredFields()) {
      return;
    }
    if (data.status === "custom" && !(data.customStatus || "").trim()) {
      toast.error("Please enter custom status");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = await createPayload(data);
      mutate(
        {
          businessId: selectedBusinessId,
          data: payload,
          isEdit: !!locationToEdit,
          locationId: locationToEdit?._id || null,
        },
        {
          onSuccess: (data) => {
            toast.success(
              data?.message || t("businessLocationSaved")
            );
            onBack();
            queryClient.invalidateQueries([
              "businessLocationData",
              selectedBusinessId,
            ]);
          },
          onError: (error) => {
            toast.error(
              error?.response?.data?.message ||
                t("failedToSaveBusinessLocation")
            );
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        }
      );
    } catch (err) {
      setIsSubmitting(false);
      console.error("Error:", err);
      toast.error(t("failedToSaveLocation"));
    }
  };

  // Helper to construct S3 URL from fileKey
  const getPreviewUrl = (fileKey) => {
    if (!fileKey) return null;
    if (fileKey.startsWith('http://') || fileKey.startsWith('https://') || fileKey.startsWith('blob:')) {
      return fileKey;
    }
    // Construct S3 URL from fileKey
    const baseUrl = 'https://beemzbucket.s3.us-east-1.amazonaws.com';
    const key = fileKey.startsWith('/') ? fileKey.substring(1) : fileKey;
    return `${baseUrl}/${key}`;
  };

  // Use state-based previews or fallback to form values
  const bannerPreviewValue = bannerPreview 
    ? (bannerPreview.startsWith('blob:') || bannerPreview.startsWith('http') 
        ? bannerPreview 
        : getPreviewUrl(bannerPreview))
    : (typeof locationBannerPhoto === "string" 
        ? getPreviewUrl(locationBannerPhoto) 
        : null);
  
  const profilePreviewValue = profilePreview 
    ? (profilePreview.startsWith('blob:') || profilePreview.startsWith('http') 
        ? profilePreview 
        : getPreviewUrl(profilePreview))
    : (typeof locationProfilePhoto === "string" 
        ? getPreviewUrl(locationProfilePhoto) 
        : null);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 4, bgcolor: "#fff" }}>
        <Button
          variant="contained"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: "lowercase" }}
        >
          {t("back")}
        </Button>

        {/* Header */}
        <Box textAlign="center" mb={4}>
          <BusinessIcon sx={{ fontSize: 48, color: "#7d3cff", mb: 2 }} />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            fontWeight="bold"
          >
            {locationToEdit ? t("editLocation") : t("addNewLocation")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textTransform: "lowercase" }}>
            {businessLocationData?.business.businessName ||
              t("yourBusinessName")}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <BusinessUploads
              handleFileUpload={handleFileUpload}
              handleFileDelete={handleFileDelete}
              bannerPreview={bannerPreviewValue}
              profilePreview={profilePreviewValue}
            />

            {/* Location Gallery Media */}
            <Grid item xs={12} mt={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                location photos and videos
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {media.map((item, index) => (
                  <Box
                    key={`${item.url}-${index}`}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid #eee",
                    }}
                  >
                    {item.type === "video" ? (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <video
                          src={item.preview || item.url}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          muted
                        />
                        <PlayCircleOutlineIcon
                          sx={{
                            position: "absolute",
                            inset: 0,
                            margin: "auto",
                            color: "#fff",
                            fontSize: 36,
                            filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.45))",
                          }}
                        />
                      </Box>
                    ) : (
                      <img
                        src={item.preview || item.url}
                        alt={`Location media ${index + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMedia(index)}
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        bgcolor: "rgba(255,255,255,0.8)",
                        "&:hover": { bgcolor: "#fff", color: "error.main" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {media.length < MAX_BUSINESS_MEDIA_ITEMS && (
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 2,
                      border: "2px dashed #ddd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      bgcolor: "#fafafa",
                      "&:hover": { borderColor: "primary.main", bgcolor: "#f0f7ff" },
                    }}
                    onClick={() =>
                      document.getElementById("location-media-upload-input")?.click()
                    }
                  >
                    <AddIcon sx={{ fontSize: 32, color: "#999" }} />
                    <input
                      id="location-media-upload-input"
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleMediaUpload}
                      style={{ display: "none" }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Business Name + Category Info */}
            <Grid item xs={12} mt={4}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: "10px",
                  border: "1px solid #ECECEC",
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 , textTransform: "lowercase"}}>
                  {businessLocationData?.business.businessName ||
                    t("yourBusinessName")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {businessLocationData?.business?.businessCategory?.name ||
                    t("yourCategoryName")}
                </Typography>
              </Box>
            </Grid>

            {/* Business Location Name */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="businessLocationName"
                control={control}
                rules={{ required: t("businessLocationNameRequired") }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("businessLocationName")}
                    placeholder={t("enterYourBusinessLocationName")}
                    error={!!errors.businessLocationName}
                    helperText={errors.businessLocationName?.message}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>

            {/* Username Field */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="businessLocationUsername"
                control={control}
                rules={{
                  required: t("businessLocationUsernameRequired"),
                  validate: (value) =>
                    usernameState.isValid || t("usernameAlreadyTaken"),
                }}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={options}
                    onInputChange={(event, value) => {
                      handleSearch(value);
                      field.onChange(value);
                    }}
                    value={field.value || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...field}
                        value={field.value || ""}
                        fullWidth
                        label={t("businessLocationUsername")}
                        placeholder={t("businessLocationUsername")}
                        error={!!errors.businessLocationUsername}
                        helperText={errors.businessLocationUsername?.message}
                        sx={inputSx}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <Box
                              component="span"
                              sx={{ color: "#888", fontWeight: 500, mr: 1 }}
                            >
                              @
                            </Box>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Physical Address Toggle */}
            <Grid item xs={12} mt={4}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: "10px",
                  border: "1px solid #ECECEC",
                }}
              >
                <Typography variant="body1" fontWeight="500">
                  {isMobileAddress ? t("mobileAddress") : t("physicalAddressLabel")}
                </Typography>
                <Controller
                  name="isMobileAddress"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={e => {
                        const newValue = e.target.checked;
                        // Only reset if the value is actually changing
                        if (field.value !== newValue) {
                          setValue("selectedUser", null);
                          setValue("locationData", { longitude: null, latitude: null, physicalAddress: "" });
                          setValue("physicalAddress", "");
                        }
                        field.onChange(newValue);
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#7d3cff" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#7d3cff" },
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* Physical Address Field */}
            {!isMobileAddress && (
              <Grid item xs={12} mt={4}>
                <AddressAutocompleteField
                  control={control}
                  name="physicalAddress"
                  label={t("physicalAddressLabel")}
                  placeholder={t("enterPhysicalAddress")}
                  rules={{ required: t("physicalAddressRequired") }}
                  errors={errors}
                  sx={inputSx}
                  onAddressSelect={handleLocationSelect}
                />
              </Grid>
            )}

            {/* Action Button */}
            <Grid item xs={12} mt={4}>
              <Controller
                name={isMobileAddress ? "selectedUser" : "locationData"}
                control={control}
                rules={
                  isMobileAddress
                    ? { required: t("pleaseSelectUserForLocation") }
                    : {}
                }
                render={({ field }) => (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        py: 2,
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "1.09rem",
                        background: "#7d3cff",
                        color: "#fff",
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { background: "#5f2bb7" },
                      }}
                      onClick={() => {
                        if (isMobileAddress) {
                          // Open user selection dialog
                          setValue("openUserList", true);
                        } else {
                          // Open location selection dialog
                          setValue("openLocation", true);
                        }
                      }}
                    >
                      {isMobileAddress
                        ? t("selectUserForLocation")
                        : t("selectBusinessAddressOnMap")}
                    </Button>
                    {errors.selectedUser && (
                      <FormHelperText sx={{ color: "error.main", ml: 1.75 }}>
                        {errors.selectedUser.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>

            {/* Selected User Display */}
            {isMobileAddress && selectedUser && (
              <Grid item xs={12}>
                <Box
                  mt={3}
                  p={2}
                  bgcolor="#f6f6fa"
                  borderRadius={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  width="100%"
                  boxShadow={1}
                  sx={{ border: "1px solid #e0e0e0" }}
                >
                  <Box display="flex" gap={2}>
                    <Avatar
                      src={files(selectedUser.profilePic, "image")}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: "#ece9f5",
                        fontWeight: 700,
                        fontSize: 20,
                      }}
                      alt={selectedUser.fullName}
                    >
                      {selectedUser.fullName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: 17, color: "#211a34" }}
                      >
                        {selectedUser.fullName}
                      </Typography>
                      <Typography
                        sx={{ color: "#7c3aed", fontSize: 15, opacity: 0.85 }}
                      >
                        @{selectedUser.userName}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    aria-label={t("close")}
                    onClick={() => setValue("selectedUser", null)}
                    sx={{ color: theme.palette.grey[500], ml: 1 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Description */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("locationDescription")}
                    placeholder={t("enterYourNewLocationDescription")}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="emailAddress"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("invalidEmailAddress"),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("emailAddress")}
                    placeholder={t("enterYourEmailAddress")}
                    type="email"
                    error={!!errors.emailAddress}
                    helperText={errors.emailAddress?.message}
                    disabled={!!locationToEdit}
                    sx={inputSx}
                    InputProps={{
                      endAdornment: locationToEdit ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setNewEmail(field.value || "");
                              setChangeEmailDialogOpen(true);
                              setEmailChangeError("");
                            }}
                            sx={{ color: "#6200EE" }}
                            size="small"
                            edge="end"
                          >
                            <EditIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}
              />
            </Grid>

            {/* Mobile with React Phone Input */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: t("mobileNumberRequired"),
                  validate: (value) => {
                    if (!value || value.length < 8) {
                      return t("pleaseEnterValidMobileNumber");
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 500 }}
                    >
                      {t("mobileNumber")}
                    </Typography>
                    <PhoneInput
                      inputStyle={{
                        width: "100%",
                        borderRadius: "12px",
                        background: "#F4F5FA",
                        height: "56px",
                      }}
                      containerStyle={{ width: "100%" }}
                      defaultCountry={"IN"}
                      value={value}
                      onChange={onChange}
                      placeholder={t("enterBusinessContactNumber")}
                    />
                    {errors.phoneNumber && (
                      <FormHelperText sx={{ color: "error.main", ml: 1.75 }}>
                        {errors.phoneNumber.message}
                      </FormHelperText>
                    )}
                  </Box>
                )}
              />
            </Grid>

            {/* Business Location Status */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: "#888", fontSize: "14px" }}>
                      {t("businessLocationStatus")}
                    </InputLabel>
                    <Select
                      {...field}
                      label={t("businessLocationStatus")}
                      sx={inputSx}
                    >
                      <MenuItem value="open">{t("businessStatusOpen")}</MenuItem>
                      <MenuItem value="close">{t("businessStatusClose")}</MenuItem>
                      <MenuItem value="custom">custom</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {status === "custom" && (
              <Grid item xs={12} mt={2}>
                <Controller
                  name="customStatus"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="custom status"
                      placeholder="enter custom status"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Business VAT Number */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="businessVatNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("businessLocationVatNumber")}
                    placeholder={t("enterBusinessLocationVatNumber")}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>

            {/* Business Tax Reference Number */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="businessTaxReferenceNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("businessLocationTaxReferenceNumber")}
                    placeholder={t("enterBusinessLocationTaxReferenceNumber")}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>

            {/* Business Registration Number */}
            <Grid item xs={12} mt={4}>
              <Controller
                name="businessRegistrationNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("businessLocationRegistrationNumber")}
                    placeholder={t("enterBusinessLocationRegistrationNumber")}
                    sx={inputSx}
                  />
                )}
              />
            </Grid>

            {/* Temporary Closed */}
            <Grid item xs={12} mt={4}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: "10px",
                  border: "1px solid #ECECEC",
                }}
              >
                <Controller
                  name="isTemporaryClosed"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={!!field.value}
                          sx={{
                            color: "#7d3cff",
                            "&.Mui-checked": { color: "#7d3cff" },
                          }}
                        />
                      }
                      label={t("temporaryClosed")}
                    />
                  )}
                />
              </Box>
            </Grid>

            {!isTemporaryClosed && (
              <>
                {/* Schedule Type */}
                <Grid item xs={12} mt={4}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "10px",
                      border: "1px solid #ECECEC",
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: "#7d3cff" }}>
                        {t("selectYourBusinessSchedule")}
                      </InputLabel>
                      <Controller
                        name="scheduleType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label={t("selectYourBusinessSchedule")}
                            sx={{
                              color: "#7d3cff",
                              ".MuiOutlinedInput-notchedOutline": {
                                borderColor: "#7d3cff",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#7d3cff",
                                },
                            }}
                          >
                            <MenuItem value="always_open">
                              {t("alwaysOpen24_7")}
                            </MenuItem>
                            <MenuItem value="custom">{t("customHours")}</MenuItem>
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Box>
                </Grid>

                {/* Schedule Days */}
                <Grid item xs={12} mt={2}>
                  <Box>
                    {scheduleDays.map((dayData, index) => (
                      <Box
                        key={dayData.day}
                        display="flex"
                        alignItems="center"
                        mb={2}
                        mt={4}
                      >
                        <Box width="150px">
                          <Typography textTransform="lowercase">
                            {t(`day${dayData.day.charAt(0).toUpperCase() + dayData.day.slice(1)}`)}
                          </Typography>
                          {!dayData.isOpen && (
                            <Typography fontSize={13} color="textSecondary">
                              {t("closed")}
                            </Typography>
                          )}
                          {dayData.isOpen && scheduleType === "always_open" && (
                            <Typography fontSize={13} color="textSecondary">
                              {t("open24Hours")}
                            </Typography>
                          )}
                        </Box>

                        <Controller
                          name={`scheduleDays.${index}.isOpen`}
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <Checkbox
                              checked={!!value}
                              onChange={(e) => onChange(e.target.checked)}
                              sx={{
                                color: "#7d3cff",
                                "&.Mui-checked": { color: "#7d3cff" },
                              }}
                            />
                          )}
                        />

                        {dayData.isOpen && scheduleType === "custom" && (
                          <>
                            <Box ml={2}>
                              <Controller
                                name={`scheduleDays.${index}.openTime`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={t("openTime")}
                                    type="time"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 300 }}
                                    sx={{ minWidth: 135 }}
                                  />
                                )}
                              />
                            </Box>
                            <Box ml={2}>
                              <Controller
                                name={`scheduleDays.${index}.closeTime`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={t("closeTime")}
                                    type="time"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 300 }}
                                    sx={{ minWidth: 135 }}
                                  />
                                )}
                              />
                            </Box>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </>
            )}

            {/* Save Button */}
            <Grid item xs={12} mt={4}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  py: 2,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  background: "#7d3cff",
                  borderRadius: "8px",
                  boxShadow: "none",
                  textTransform: "lowercase",
                  "&:hover": {
                    background: "#5f2bb7",
                  },
                }}
              >
                {isSubmitting ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={20} color="inherit" />
                    {t("saving")}
                  </Box>
                ) : (
                  t("save")
                )}
              </Button>
            </Grid>
          </Grid>

          {/* Dialogs */}
          <SelectLocation
            open={watchedValues.openLocation || false}
            setOpen={(open) => setValue("openLocation", open)}
            onSelect={handleLocationSelect}
          />
          <SelectUser
            linkedUserList={linkedUserList}
            open={watchedValues.openUserList || false}
            onClose={() => setValue("openUserList", false)}
            selectedUser={selectedUser}
            setSelectedUser={(user) => setValue("selectedUser", user)}
          />
          
          {/* Change Email Dialog */}
          <ChangeEmailDialog
            open={changeEmailDialogOpen}
            onClose={() => {
              setChangeEmailDialogOpen(false);
              setEmailChangeError("");
            }}
            currentEmail={watchedValues.emailAddress || ""}
            onSendOtp={(email) => {
              setNewEmail(email);
              sendOtpMutation.mutate(email);
            }}
            isLoading={sendOtpMutation.isLoading}
            errorMessage={emailChangeError}
          />
          
          {/* OTP Verification Dialog */}
          <OtpVerificationDialog
            open={otpDialogOpen}
            onClose={() => {
              setOtpDialogOpen(false);
              setNewEmail("");
            }}
            onVerify={(otp) => {
              verifyOtpMutation.mutate(otp);
            }}
            onResend={() => {
              sendOtpMutation.mutate(newEmail);
            }}
            isVerifying={verifyOtpMutation.isLoading}
            isResending={sendOtpMutation.isLoading}
            resendInitialSeconds={60}
            email={newEmail}
            isMaxView={false}
            errorMessage={
              verifyOtpMutation.error?.response?.data?.message || ""
            }
          />
        </form>
      </Paper>
    </Container>
  );
};

export default AddLocation;
